import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import DatePicker from "react-datepicker";
import { BsCalendar } from 'react-icons/bs';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import '../styles/main.css';
import { apiGet, apiPost } from '../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const UserLoginHistory = () => {
  const [filters, setFilters] = useState({
    userName: '',
    followedBy: '',
    group: null,
    taskCoordinator: null,
    loginTime: ''
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [taskCoordinators, setTaskCoordinators] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const columns = [
    {
      name: 'User Name',
      selector: row => row.userName,
      sortable: true,
     
          center:true,
   style: {
      borderRight: '1px solid #dee2e6',
      textAlign:"center"
    },
    },
   {
  name: 'Followed By',
  selector: row => row.followedBy,
  sortable: true,
  center: true,
  cell: row => (
    <div className="followed-by-cell">
      {row.followedBy}
    </div>
  ),
  style: {
    borderRight: '1px solid #dee2e6',
    justifyContent: "center",
    textAlign: "center"
  }
}
,
    {
      name: 'Group',
      selector: row => row.group,
      sortable: true,
      
           center:true,
   style: {
      borderRight: '1px solid #dee2e6',

    },
    },
    {
      name: 'Login Time',
      selector: row => row.loginTime,
      sortable: true,
      cell: row => row.loginTime ? new Date(row.loginTime).toLocaleString() : 'N/A',
   
         center:true,
   style: {
       borderRight: '1px solid #dee2e6',

    },
    },
    {
      name: 'Logout Time',
      selector: row => row.logoutTime,
      sortable: true,
      cell: row => row.logoutTime ? new Date(row.logoutTime).toLocaleString() : 'N/A',
      
         center:true,

    }
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [coordinatorsResponse, groupsResponse] = await Promise.all([
          apiGet('/User/GetAllUsers'),
          apiGet('/Group/GetAll')
        ]);

        if (coordinatorsResponse?.isSuccess) {
          setTaskCoordinators(
            coordinatorsResponse.data
              .filter(user => user.roleName?.toLowerCase() === 'taskcoordinator')
              .map(user => ({
                value: user.userID,
                label: user.username
              }))
          );
        }

        if (groupsResponse?.isSuccess) {
          setGroups(
            groupsResponse.data.map(group => ({
              value: group.groupID,
              label: group.name
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleGroupChange = selectedOption => {
    setFilters(prev => ({ ...prev, group: selectedOption }));
  };

  const handleCoordinatorChange = selectedOption => {
    setFilters(prev => ({ ...prev, taskCoordinator: selectedOption }));
  };

  const handleSearch = async () => {
    setIsTableLoading(true);
    try {
      const payload = {
        GroupID: filters.group?.value || null,
        TaskCoordinatorID: filters.taskCoordinator?.value || null,
        StartDate: startDate ? startDate.toISOString() : null,
        EndDate: endDate ? endDate.toISOString() : null
      };

      const response = await apiPost('/auth/GetUserLoginHistory', payload);
      
      if (Array.isArray(response)) {
        setTableData(response.map(item => ({
          userName: item.userName ?? 'N/A',
          followedBy: item.followedBy ?? 'N/A',
          group: item.groupName ?? 'N/A',
          loginTime: item.loginTime,
          logoutTime: item.logoutTime
        })));
      } else {
        console.error('Unexpected response format:', response);
        setTableData([]);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
      setTableData([]);
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({
      userName: '',
      followedBy: '',
      group: null,
      taskCoordinator: null,
      loginTime: ''
    });
    setStartDate(null);
    setEndDate(null);
    setTableData([]);
  };

  const exportToPDF = () => {
  if (tableData.length === 0) {
    alert('No data available to export');
    return;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const leftMargin = 15;


  const title = 'User Login History Report';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  const titleWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - titleWidth) / 2, 15);


  const now = new Date();
  const formattedDate = now.toLocaleDateString('en-GB'); 
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const dateText = `Date: ${formattedDate}`;
  const dateWidth = doc.getTextWidth(dateText);
  doc.text(dateText, pageWidth - leftMargin - dateWidth, 15);

  let yPosition = 25;


  if (filters.group) {
    doc.text(`Group: ${filters.group.label}`, leftMargin, yPosition);
    yPosition += 5;
  }

  if (filters.taskCoordinator) {
    doc.text(`Task Coordinator: ${filters.taskCoordinator.label}`, leftMargin, yPosition);
    yPosition += 5;
  }

  if (startDate || endDate) {
    const start = startDate ? startDate.toLocaleDateString('en-GB') : 'N/A';
    const end = endDate ? endDate.toLocaleDateString('en-GB') : 'N/A';
    doc.text(`Date Range: ${start} to ${end}`, leftMargin, yPosition);
    yPosition += 8;
  } else {
    yPosition += 5;
  }


  const headers = ['User Name', 'Followed By', 'Group', 'Login Time', 'Logout Time'];
  const data = tableData.map(item => [
    item.userName,
    item.followedBy,
    item.group,
    item.loginTime ? new Date(item.loginTime).toLocaleString('en-GB') : 'N/A',
    item.logoutTime ? new Date(item.logoutTime).toLocaleString('en-GB') : 'N/A'
  ]);


  autoTable(doc, {
    head: [headers],
    body: data,
    startY: yPosition,
    margin: { left: leftMargin, right: leftMargin },
    styles: {
      fontSize: 9,
      halign: 'center',
      cellPadding: 4
    },
    headStyles: {
      fillColor: [44, 62, 80],
      textColor: 255,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 50 },
      2: { cellWidth: 40 },
      3: { cellWidth: 60 },
      4: { cellWidth: 60 }
    }
  });

 
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100);

   
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - leftMargin, 200, { align: 'right' });


    const generatedText = `Generated on: ${now.toLocaleString('en-GB')}`;
    doc.text(generatedText, leftMargin, 200);
  }

  doc.save(`User_Login_History_${Date.now()}.pdf`);
};


  const exportToExcel = () => {
    if (tableData.length === 0) {
      alert('No data available to export');
      return;
    }

    
    const excelData = tableData.map(item => ({
      'User Name': item.userName,
      'Followed By': item.followedBy,
      'Group': item.group,
      'Login Time': item.loginTime ? new Date(item.loginTime).toLocaleString() : 'N/A',
      'Logout Time': item.logoutTime ? new Date(item.logoutTime).toLocaleString() : 'N/A'
    }));

 
    const ws = XLSX.utils.json_to_sheet(excelData);
    
  
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'UserLoginHistory');
    
 
    XLSX.writeFile(wb, `User_Login_History_${new Date().getTime()}.xlsx`);
  };

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#2c3e50',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '14px',
      },
    },
    rows: {
      style: {
        fontSize: '14px',
        '&:hover': {
          backgroundColor: '#f8f9fa'
        }
      },
    },
    pagination: {
      style: {
        borderTop: 'none'
      }
    }
  };

  return (
    <div className="container mt-4 mb-4">
     <div className="tabs-section-wrapper mb-2">
             <div className='row '>
 <div className="d-flex justify-content-lg-center d-block tabs-section gap-3 mb-2">
        <Link to="/dashboard/reports">
          <Button variant="light" className="btn-tab btn-btn-sechover">Task Detail</Button>
        </Link>
        <Link to="/dashboard/UserLoginHistory">
          <Button variant="primary"  className="btn-tab  btn-btn-primary">User Login History</Button>
        </Link>
        <Link to="/dashboard/UserTaskUpdate">
          <Button variant="light" className="btn-tab btn-btn-sechover">User Task Update</Button>
        </Link>
        <Link to="/dashboard/UserFrequency">
          <Button variant="light" className="btn-tab btn-btn-sechover">User Frequency</Button>
        </Link>
         <Link to="/dashboard/voiceSearch"><Button variant="light" className="btn-tab">VoiceSearch</Button></Link>
      </div>
</div>
</div>

      <div className="row custombox">
        <div className="rounded p-4 mb-4">
          <div className="text-center customheader">
            <h5 className="mb-0">User Login History</h5>
          </div>

          <Form>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label>Group</Form.Label>
                <Select
                  options={groups}
                  isLoading={isLoading}
                  onChange={handleGroupChange}
                  value={filters.group}
                  placeholder="Select Group"
                  isClearable
                  isSearchable
                />
              </Col>
              <Col md={3}>
                <Form.Label>Task Coordinator</Form.Label>
                <Select
                  options={taskCoordinators}
                  isLoading={isLoading}
                  onChange={handleCoordinatorChange}
                  value={filters.taskCoordinator}
                  placeholder="Select Task Coordinator"
                  isClearable
                  isSearchable
                />
              </Col>
              <Col md={3}>
                <Form.Label>Start Date</Form.Label>
                <InputGroup>
                  <DatePicker
                    selected={startDate}
                    onChange={date => setStartDate(date)}
                    className="form-control"
                    placeholderText="Select start date"
                    ref={startDateRef}
                    maxDate={endDate || new Date()}
                    selectsStart
                    startDate={startDate}
                     dateFormat="dd-MM-yyyy"
                    endDate={endDate}
                  />
                  <InputGroup.Text
                    style={{ cursor: 'pointer' }}
                    onClick={() => startDateRef.current?.setFocus()}
                  >
                    <BsCalendar />
                  </InputGroup.Text>
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Label>End Date</Form.Label>
                <InputGroup>
                  <DatePicker
                    selected={endDate}
                    onChange={date => setEndDate(date)}
                    className="form-control"
                    placeholderText="Select end date"
                    ref={endDateRef}
                    minDate={startDate}
                    maxDate={new Date()}
                     dateFormat="dd-MM-yyyy"
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                  />
                  <InputGroup.Text
                    style={{ cursor: 'pointer' }}
                    onClick={() => endDateRef.current?.setFocus()}
                  >
                    <BsCalendar />
                  </InputGroup.Text>
                </InputGroup>
              </Col>
            </Row>

            <div className="text-center mt-4">
              <Button 
                variant="primary" 
                className="px-4 me-3 btn-fontweight btn-btn-primary"
                onClick={handleSearch}
                
              >
                {isTableLoading ? 'Searching...' : 'Search'}
              </Button>
              <Button 
                variant="secondary" 
                className="px-4 btn-fontweight"
                onClick={handleClear}
              
              >
                Clear
              </Button>
            </div>
          </Form>
        </div>
      </div>

      
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <Button 
             className="me-2 btn-fontweight custompdf-btn"
              onClick={exportToPDF}
              disabled={tableData.length === 0}
            >
              Export to PDF
            </Button>
            <Button 
              className='btn-fontweight customexport-btn'
              onClick={exportToExcel}
              disabled={tableData.length === 0}
            >
              Export to Excel
            </Button>
          </div>
        </div>
        
        <div className="card shadow-sm rounded-4 py-4 px-0  border-0 bg-white mb-custom">
          <DataTable
            columns={columns}
           data={tableData.map((dept, idx) => ({
    ...dept,
    _rowIndex: idx, 
  }))}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            striped
            responsive
            progressPending={isTableLoading}
            progressComponent={<div className="text-center my-4">Loading data...</div>}
            noDataComponent={<div className="text-center my-4">No records found</div>}
            className=" customuser-data-table customaruerlogin-data-table dashboard-table"
      customStyles={{
              table: {
                style: {
              fontWeight: '600',
      fontSize: '14px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                    textAlign: 'center',

                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                    textAlign: 'center',
                  color: "white",
                  textTransform: "uppercase",
                   textAlign: columns.name === "Issue/Action/Decision" ? "center" : "left",
                },
              },
           
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
      conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]}  
          />
        </div>
      </div>
    </div>
  );
};

export default UserLoginHistory;